import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useLoaderData } from 'react-router-dom';
import { API, AppConstants, Utils } from '@enrich/shared-lib';
import QueryString from 'qs';

const SORT_ACCESSORS = {
    'contact.lastName': (c) => c.lastName ?? '',
    enrichProgramStatus: (c) => c.enrichProgramStatus ?? '',
    'user.lastLogin': (c) => c.user?.lastLogin ?? '',
};

export const SchoolNonActiveStudents = () => {
    const school = Utils.deepFlatten(useLoaderData());
    const stepOptions = [25, 50, 100];
    const [students, setStudents] = useState(school.contacts);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);
    const [sort, setSort] = useState();

    useEffect(() => {
        if (!page) return;

        const accessor = sort && SORT_ACCESSORS[sort.property];
        const sortedContacts = accessor
            ? [...school.contacts].sort((a, b) => {
                  const av = accessor(a);
                  const bv = accessor(b);
                  const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                  return sort.direction === 'desc' ? -cmp : cmp;
              })
            : school.contacts;

        const pagedStudents = sortedContacts.slice(
            page * pageSize - pageSize,
            page * pageSize,
        );

        setPage(page);
        setStudents(pagedStudents);
        setTotalItems(school.contacts.length);
    }, [page, pageSize, sort]);

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setPage(1);
    };

    const contactDataColumns = [
        {
            header: 'Name',
            property: 'contact.lastName',
            render: (contact) => {
                return (
                    <Anchor
                        href={`/contacts/${contact.id}`}
                        label={`${contact.firstName} ${contact.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Status',
            property: 'enrichProgramStatus',
            sortable: true,
        },
        {
            header: 'Majors',
            property: 'contact.majors',
            render: (contact) => {
                const majors = contact?.majors?.map(
                    (major) => major.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
        {
            header: 'Last Login',
            property: 'user.lastLogin',
            render: (contact) => {
                if (!contact.user?.lastLogin) return '';
                return new Date(contact.user?.lastLogin).toLocaleString(
                    undefined,
                    {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    },
                );
            },
            sortable: true,
        },
    ];

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize != newPageSize) {
            setPageSize(newPageSize);
        }
        setPage(page);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="2">{school.name}</Heading>
                </Box>

                <Box>
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">Non-Active Students</Heading>
                    </Box>

                    <Box elevation="large" pad="medium" fill="horizontal">
                        <DataTable
                            primaryKey="id"
                            sort={sort}
                            onSort={handleSort}
                            data={students}
                            columns={contactDataColumns}
                        />
                    </Box>
                </Box>

                <Box align="center" justify="center" pad="small">
                    <Pagination
                        numberItems={totalItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handlePageChange({
                                page,
                                startIndex,
                                endIndex,
                            });
                        }}
                        step={pageSize}
                        stepOptions={stepOptions}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

export async function loader({ params }) {
    const query = QueryString.stringify({
        populate: {
            contacts: {
                filters: {
                    enrichProgramStatus: {
                        $in: AppConstants.nonActiveStudentStatuses,
                    },
                },
                populate: {
                    majors: {
                        fields: ['name'],
                    },
                    user: {
                        fields: ['lastLogin'],
                    },
                },
                sort: ['updatedAt:desc'],
            },
        },
    });

    try {
        const response = await API.get(
            `/api/schools/${params.schoolId}?${query}`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}
